import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  Paper,
  Container,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import Edit_Icon from "../../images/table/Edit_Icon.svg";

import SubTitle from "../common/SubTitle";
import config from "../../config.js";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { width } from "@fortawesome/free-brands-svg-icons/fa42Group";
import Upload_Icon from "../../images/vehicle/Upload_icon.svg";
import { useTranslation } from "react-i18next";
import { useUser } from "../../pages/login/UserContextPersistent";
export default function OrganizationMain({ starting }) {
  const navigate = useNavigate();
  const { dispatch, state } = useUser();

  const { t } = useTranslation();
  const [organizationImage, setOrganizationImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageUploadError, setImageUploadError] = useState("");

  const { state: userState } = useUser();
  const [orgId, setOrgId] = useState("");
  const [orgData, setOrgData] = useState({});
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [backendErrors, setBackendErrors] = useState({
    name: "",
    domain: "",
    contactEmail: "",
    contactPhone: "",
    pincode: "",
    general: "",
    transportEmail: "",
    transportPhoneNumber: "",
  });

  const organizationName = useRef(null);
  const organizationCountry = useRef(null);
  const contactNameRef = useRef(null);
  const contactEmailRef = useRef(null);
  const contactPhoneRef = useRef(null);
  const pincodeRef = useRef(null);
  const transportPhoneNumberRef = useRef(null);

  const fetchData = async () => {
    const userState = localStorage.getItem("userState");

    if (userState) {
      const parsedUserState = JSON.parse(userState);

      if (parsedUserState?.user?.organizationId) {
        setOrgId(parsedUserState.user.organizationId);

        try {
          setLoading(true);
          const response = await axios.get(
            `${config.baseURL}/organization/${parsedUserState.user.organizationId}`
          );
          console.log("Data:", response.data.organization);
          setOrgData(response.data.organization);
          setFormData(response.data.organization);
          if (
            response.data.organization.documents &&
            Array.isArray(response.data.organization.documents)
          ) {
            const organizationDoc = response.data.organization.documents.find(
              (doc) => doc.name === "Organization" && doc.location
            );
            if (organizationDoc && organizationDoc.location) {
              try {
                setImagePreview(state?.organization?.imageBlobUrl);
              } catch (e) {
                console.error("Error fetching organization document:", e);
              }
            }
          }
        } catch (error) {
          console.error("Error fetching organization data:", error);
          setNotification({
            open: true,
            message: "Failed to load organization data",
            severity: "error",
          });
        } finally {
          setLoading(false);
        }
      }
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchImage = async (imageUrl) => {
    try {
      const response = await axios.get(imageUrl, {
        responseType: "arraybuffer",
        withCredentials: true,
      });
      const blob = new Blob([response.data], { type: "image/jpeg" });
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error("Error fetching image:", error);
      return null;
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    // Mark field as touched when user starts typing
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));

    // Clear backend errors when user starts typing
    if (backendErrors[name]) {
      setBackendErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    // Validate on change
    const error = validateField(name, value);
    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));

    const error = validateField(name, value);
    if (error) {
      setErrors((prev) => ({
        ...prev,
        [name]: error,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Reset all errors
    setErrors({});
    setBackendErrors({
      name: "",
      domain: "",
      contactEmail: "",
      contactPhone: "",
      pincode: "",
      general: "",
      transportEmail: "",
      transportPhoneNumber: "",
    });

    // Validate all required fields
    const requiredFields = [
      "name",
      "domain",
      "country",
      "contactName",
      "contactPhone",
      "contactEmail",
    ];

    let newErrors = {};
    let newTouched = {};
    let hasErrors = false;

    requiredFields.forEach((field) => {
      newTouched[field] = true;
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
        hasErrors = true;
      }
    });

    // Validate field formats
    Object.keys(formData).forEach((field) => {
      if (!requiredFields.includes(field) && formData[field]) {
        const error = validateField(field, formData[field]);
        if (error) {
          newErrors[field] = error;
          newTouched[field] = true;
          hasErrors = true;
        }
      }
    });

    setErrors(newErrors);
    setTouched(newTouched);

    if (hasErrors) {
      setNotification({
        open: true,
        message: "Please fix all validation errors before submitting",
        severity: "error",
      });

      // Focus on first error field
      if (newErrors.name) {
        organizationName?.current?.focus();
      } else if (newErrors.domain) {
        // Add ref for domain field and focus
      } else if (newErrors.country) {
        organizationCountry?.current?.focus();
      } else if (newErrors.contactName) {
        contactNameRef?.current?.focus();
      } else if (newErrors.contactEmail) {
        contactEmailRef?.current?.focus();
      } else if (newErrors.contactPhone) {
        contactPhoneRef?.current?.focus();
      } else if (newErrors.pincode) {
        pincodeRef?.current?.focus();
      } else if (newErrors.transportPhoneNumber) {
        transportPhoneNumberRef?.current?.focus();
      }

      return;
    }

    try {
      const userState = localStorage.getItem("userState");
      const parsedUserState = JSON.parse(userState);
      setIsSubmitting(true);
      const response = await axios.put(
        `${config.baseURL}/organization/${parsedUserState.user.organizationId}`,
        formData
      );

      if (response.data.organization) {
        setOrgData(response.data.organization);

        setNotification({
          open: true,
          message: t("Organization updated successfully"),
          severity: "success",
        });
        const imageRespons = await uploadOrganizationLogo(
          parsedUserState.user.organizationId
        );

        let imageBlobUrl = null;

        if (imageRespons && imageRespons?.location) {
          try {
            const logoLocation = imageRespons?.location;
            if (logoLocation) {
              const imageUrl = `${config.baseURL}/upload/image?fileName=${logoLocation}`;
              imageBlobUrl = await fetchImage(imageUrl);
            }
          } catch (imgErr) {
            imageBlobUrl = null;
          }
        }

        dispatch({
          type: "UPDATE_ORGANIZATION",
          payload: {
            name: response.data.organization.name,
            address: response.data.organization.address,
            contactName: response.data.organization.contactName,
            ...(imageRespons?.location && {
              logoLocation: imageRespons.location,
              imageBlobUrl,
            }),
          },
        });
      }
    } catch (error) {
      console.error("Error updating organization:", error);

      if (error.response?.data?.error) {
        const errorMessage = error.response.data.error;

        if (errorMessage.includes("Validation failed:")) {
          const validationErrors = errorMessage
            .split("Validation failed:")[1]
            .split(",")
            .map((err) => err.trim());

          validationErrors.forEach((error) => {
            if (error.includes("name already exists")) {
              setBackendErrors((prev) => ({
                ...prev,
                name: "Organization name already exists",
              }));
            }
            if (error.includes("domain already registered")) {
              setBackendErrors((prev) => ({
                ...prev,
                domain: "Domain already registered",
              }));
            }
            if (error.includes("email already registered")) {
              setBackendErrors((prev) => ({
                ...prev,
                contactEmail: "Email already registered",
              }));
            }
            if (error.includes("Invalid domain format")) {
              setBackendErrors((prev) => ({
                ...prev,
                domain: "Invalid domain format",
              }));
            }
            if (error.includes("Invalid pincode format")) {
              setBackendErrors((prev) => ({
                ...prev,
                pincode: "Pincode must be 6 digits",
              }));
            }
            if (error.includes("Invalid transport email format")) {
              setBackendErrors((prev) => ({
                ...prev,
                transportEmail: "Invalid transport email format",
              }));
            }
            if (error.includes("Invalid transport phone number format")) {
              setBackendErrors((prev) => ({
                ...prev,
                transportPhoneNumber: "Invalid transport phone number format",
              }));
            }
          });

          const otherErrors = validationErrors
            .filter(
              (err) =>
                !err.includes("name already exists") &&
                !err.includes("domain already registered") &&
                !err.includes("email already registered") &&
                !err.includes("Invalid domain format") &&
                !err.includes("Invalid pincode format") &&
                !err.includes("Invalid transport email format") &&
                !err.includes("Invalid transport phone number format")
            )
            .join(", ");

          if (otherErrors) {
            setBackendErrors((prev) => ({
              ...prev,
              general: otherErrors,
            }));
            setNotification({
              open: true,
              message: otherErrors,
              severity: "error",
            });
          }
        } else {
          setNotification({
            open: true,
            message: errorMessage,
            severity: "error",
          });
        }
      } else {
        setNotification({
          open: true,
          message: t("Failed to update organization"),
          severity: "error",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!notification.open) return;
    // Keep error messages visible until the user submits again or navigates away;
    // success toasts can auto-dismiss so the screen does not stay cluttered.
    if (notification.severity === "error") return;

    const timer = setTimeout(() => {
      setNotification((prev) => ({
        ...prev,
        open: false,
      }));
    }, 3000);

    return () => clearTimeout(timer);
  }, [notification.open, notification.severity]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setOrganizationImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadOrganizationLogo = async (organizationId) => {
    console.log(
      "Uploading Organization Logo for organizationId:",
      organizationId
    );
    if (!organizationImage) return; // No image to upload

    const formData = new FormData();
    formData.append("image", organizationImage);
    console.log("checking formData:", formData);
    try {
      const response = await axios.post(
        `${config.baseURL}/upload/image?name=Organization&associateEntityId=${organizationId}&associatedWith=OrganizationLogo`,
        formData,
        {
          withCredentials: true,
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      // console.log("Image uploaded successfully:", response.data);

      return response.data; // Return the response data if needed
    } catch (error) {
      console.error("Error uploading image:", error);
      setImageUploadError(
        "Failed to upload Organization Logo. Please try again."
      );
    }
  };

  // Reset form to original data
  const handleCancel = () => {
    setFormData(orgData);
    navigate(-1);
  };
  const handleViewMyFeatures = () => {
    navigate(`/organization/feature/${orgId}`);
  };

  const validateField = (name, value) => {
    const requiredFields = [
      "name",
      "domain",
      "country",
      "contactName",
      "contactPhone",
      "contactEmail",
    ];

    if (requiredFields.includes(name) && (!value || value.trim() === "")) {
      return `${name.charAt(0).toUpperCase() + name.slice(1)} is required`;
    }
    switch (name) {
      case "domain":
        if (!value) return ""; // Already handled by required check
        const domainPattern =
          /^(https?:\/\/)?([A-Za-z0-9-]+\.)+[A-Za-z0-9-]+(\/.*)?$/;
        return !domainPattern.test(value) ? "Invalid domain format" : "";

      case "contactEmail":
      case "transportEmail":
        if (!value) return ""; // Only validate format if value exists
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return !emailPattern.test(value)
          ? "Please enter a valid email address (e.g., name@example.com)"
          : "";

      case "contactPhone":
        if (!value) return "Contact phone is required";
        const phonePattern = /^\+?[\d\s-]{10,}$/;
        return !phonePattern.test(value)
          ? "Please enter a valid phone number with at least 10 digits"
          : "";

      case "transportPhoneNumber":
        if (!value) return ""; // Optional field
        if (value.length !== 10) {
          return "Transport phone number must be 10 digits";
        }
        const transportPhonePattern = /^\d{10}$/;
        return !transportPhonePattern.test(value)
          ? "Please enter a valid 10-digit phone number"
          : "";

      case "pincode":
        if (!value) return ""; // Optional field
        const pincodePattern = /^\d{6}$/;
        return !pincodePattern.test(value) ? "Pincode must be 6 digits" : "";

      default:
        return "";
    }
  };

  const hasPermission = (requiredPath, requiredAction) => {
    if (!userState?.features) {
      return false; // Default to no permission if features are not available
    }
    return userState.features.some((feature) => {
      return (
        feature.api_path === requiredPath && feature.action === requiredAction
      );
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        Loading organization data...
      </Box>
    );
  }
  console.log(typeof starting, "starting value:", starting);
  const orgName = JSON.parse(localStorage.getItem("userState"))?.organization
    ?.name;

  return (
    <>
      {!starting && (
        <SubTitle
          title="Organization Profile"
          path={`${orgName || "organization"} > myOrganization`}
          track={"My Features"}
          onTrackClick={handleViewMyFeatures}
        />
      )}
      <Box sx={{ margin: "0px 40px" }}>
        {/* Single main box with shadow containing all sections */}
        <Paper
          sx={{ borderRadius: "8px", overflow: "hidden", padding: "20px" }}
        >
          <Box
            component="form"
            noValidate
            onSubmit={handleSubmit}
            sx={{ mt: 1 }}
          >
            {backendErrors.general && (
              <Box
                sx={{
                  backgroundColor: "#FDE7E7",
                  color: "#D32F2F",
                  padding: "16px",
                  borderRadius: "8px",
                  marginBottom: "20px",
                }}
              >
                <Typography>{backendErrors.general}</Typography>
              </Box>
            )}
            {notification.open && (
              <Box
                sx={{
                  backgroundColor:
                    notification.severity === "success" ? "#E8F5E9" : "#FDE7E7",
                  color:
                    notification.severity === "success" ? "#2E7D32" : "#D32F2F",
                  padding: "16px",
                  borderRadius: "8px",
                  marginBottom: "20px",
                  textAlign: "center",
                }}
              >
                <Typography>{notification.message}</Typography>
              </Box>
            )}
            {/* Organization Details Section */}
            <Box>
              <Box
                sx={{
                  width: "100%",
                  height: 52,
                  gap: 16,
                  pt: 1.5,
                  px: 3,
                  pb: 1.5,
                  borderRadius: "8px",
                  bgcolor: "#647490",
                  boxShadow: "-2px 2px 5px 0px #00000040",
                  color: "white",
                  fontSize: "24px",
                  fontWeight: "Medium",
                }}
              >
                {t("Organization Details")}
              </Box>

              <Box sx={{ p: 3 }}>
                <Grid container spacing={"20px"}>
                  {/* First row - Organization Name, Website, Country - Equal width */}
                  <Grid item xs={12} md={4}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 1,
                        fontFamily: "Roboto",
                        fontWeight: 400,
                        fontSize: "20px",
                        color: "#414958",
                        fontStyle: "Regular",
                      }}
                    >
                      Organization Name{" "}
                      <span style={{ color: "#EE3B2B" }}>*</span>
                    </Typography>
                    <TextField
                      required
                      fullWidth
                      value={formData?.name || ""}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      error={
                        touched.name && (!!errors.name || !!backendErrors.name)
                      }
                      id="name"
                      placeholder={t("Enter Organization Name (e.g.KINO)")}
                      name="name"
                      variant="outlined"
                      inputRef={organizationName}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        style: {
                          fontFamily: "Roboto",
                          fontWeight: 500,
                          fontSize: "20px",
                          color: "#111214",
                        },
                      }}
                      sx={{
                        padding: "0px 24px",
                        border:
                          touched.name && (errors.name || backendErrors.name)
                            ? "2px solid #EE3B2B"
                            : "2px solid #183059",
                        borderRadius: "16px",
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            border: "none",
                          },
                        },
                      }}
                      disabled={
                        !starting &&
                        !hasPermission("/api/user/organization", "PATCH")
                      }
                    />
                    {touched.name && (errors.name || backendErrors.name) && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#EE3B2B",
                          fontSize: "0.75rem",
                          marginTop: "4px",
                          marginLeft: "14px",
                          display: "block",
                        }}
                      >
                        {errors.name || backendErrors.name}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 1,
                        fontFamily: "Roboto",
                        fontWeight: 400,
                        fontSize: "20px",
                        color: "#414958",
                        fontStyle: "Regular",
                      }}
                    >
                      {t("City")}
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.city || ""}
                      onChange={handleInputChange}
                      id="city"
                      placeholder={t("Enter City (e.g.Bengaluru)")}
                      name="city"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        style: {
                          fontFamily: "Roboto",
                          fontWeight: 500,
                          fontSize: "20px",
                          color: "#111214",
                        },
                      }}
                      sx={{
                        border: "2px solid #183059",
                        padding: "0px 24px",
                        borderRadius: "16px",
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            border: "none",
                          },
                        },
                      }}
                      disabled={
                        !starting &&
                        !hasPermission("/api/user/organization", "PATCH")
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 1,
                        fontFamily: "Roboto",
                        fontWeight: 400,
                        fontSize: "20px",
                        color: "#414958",
                        fontStyle: "Regular",
                      }}
                    >
                      {t("Organization Website")}
                    </Typography>
                    <Box>
                      <TextField
                        required
                        fullWidth
                        id="domain"
                        value={formData.domain || ""}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        error={
                          touched.domain &&
                          (!!errors.domain || !!backendErrors.domain)
                        }
                        placeholder={t("Enter Website (e.g.kino.com)")}
                        name="domain"
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          style: {
                            fontFamily: "Roboto",
                            fontWeight: 500,
                            fontSize: "20px",
                            color: "#111214",
                          },
                        }}
                        sx={{
                          padding: "0px 24px",
                          border:
                            touched.domain &&
                            (errors.domain || backendErrors.domain)
                              ? "2px solid #EE3B2B"
                              : "2px solid #183059",
                          borderRadius: "16px",
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              border: "none",
                            },
                          },
                        }}
                        disabled={
                          !starting &&
                          !hasPermission("/api/user/organization", "PATCH")
                        }
                      />
                      {touched.domain &&
                        (errors.domain || backendErrors.domain) && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#EE3B2B",
                              fontSize: "0.75rem",
                              marginTop: "4px",
                              marginLeft: "14px",
                              display: "block",
                            }}
                          >
                            {errors.domain || backendErrors.domain}
                          </Typography>
                        )}
                    </Box>
                  </Grid>

                  {/* Left side: City, State, Address, Pincode */}
                  <Grid item xs={12} md={8}>
                    <Grid container spacing={"20px"}>
                      {/* Row with City and State */}
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            mb: 1,
                            fontFamily: "Roboto",
                            fontWeight: 400,
                            fontSize: "20px",
                            color: "#414958",
                            fontStyle: "Regular",
                          }}
                        >
                          {t("Country")}{" "}
                          <span style={{ color: "#EE3B2B" }}>*</span>
                        </Typography>
                        <TextField
                          required
                          fullWidth
                          value={formData.country || ""}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          error={
                            touched.country &&
                            (!!errors.country || !!backendErrors.country)
                          }
                          id="country"
                          inputRef={organizationCountry}
                          placeholder={t("Enter Country (e.g.India)")}
                          name="country"
                          variant="outlined"
                          InputLabelProps={{ shrink: true }}
                          inputProps={{
                            style: {
                              fontFamily: "Roboto",
                              fontWeight: 500,
                              fontSize: "20px",
                              color: "#111214",
                            },
                          }}
                          sx={{
                            padding: "0px 24px",
                            border:
                              touched.country &&
                              (errors.country || backendErrors.country)
                                ? "2px solid #EE3B2B"
                                : "2px solid #183059",
                            borderRadius: "16px",
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                border: "none",
                              },
                            },
                          }}
                          disabled={
                            !starting &&
                            !hasPermission("/api/user/organization", "PATCH")
                          }
                        />
                        {touched.country &&
                          (errors.country || backendErrors.country) && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#EE3B2B",
                                fontSize: "0.75rem",
                                marginTop: "4px",
                                marginLeft: "14px",
                                display: "block",
                              }}
                            >
                              {errors.country || backendErrors.country}
                            </Typography>
                          )}
                      </Grid>

                      {/* Row with Address and Pincode */}
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            mb: 1,
                            fontFamily: "Roboto",
                            fontWeight: 400,
                            fontSize: "20px",
                            color: "#414958",
                            fontStyle: "Regular",
                          }}
                        >
                          {t("Address")}
                        </Typography>
                        <TextField
                          required
                          fullWidth
                          id="address"
                          value={formData.address || ""}
                          onChange={handleInputChange}
                          placeholder={t("Enter Address (e.g.123 MG Road)")}
                          name="address"
                          variant="outlined"
                          InputLabelProps={{ shrink: true }}
                          inputProps={{
                            style: {
                              fontFamily: "Roboto",
                              fontWeight: 500,
                              fontSize: "20px",
                              color: "#111214",
                            },
                          }}
                          sx={{
                            padding: "0px 24px",
                            border: "2px solid #183059",
                            borderRadius: "16px",
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                border: "none",
                              },
                            },
                          }}
                          disabled={
                            !starting &&
                            !hasPermission("/api/user/organization", "PATCH")
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            mb: 1,
                            fontFamily: "Roboto",
                            fontWeight: 400,
                            fontSize: "20px",
                            color: "#414958",
                            fontStyle: "Regular",
                          }}
                        >
                          {t("State")}
                        </Typography>
                        <TextField
                          required
                          fullWidth
                          value={formData.state || ""}
                          onChange={handleInputChange}
                          id="state"
                          placeholder={t("Enter State (e.g.Karnataka)")}
                          name="state"
                          variant="outlined"
                          InputLabelProps={{ shrink: true }}
                          inputProps={{
                            style: {
                              fontFamily: "Roboto",
                              fontWeight: 500,
                              fontSize: "20px",
                              color: "#111214",
                            },
                          }}
                          sx={{
                            padding: "0px 24px",
                            border: "2px solid #183059",
                            borderRadius: "16px",
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                border: "none",
                              },
                            },
                          }}
                          disabled={
                            !starting &&
                            !hasPermission("/api/user/organization", "PATCH")
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            mb: 1,
                            fontFamily: "Roboto",
                            fontWeight: 400,
                            fontSize: "20px",
                            color: "#414958",
                            fontStyle: "Regular",
                          }}
                        >
                          {t("PIN Code")}
                        </Typography>
                        <Box>
                          <TextField
                            required
                            fullWidth
                            id="pincode"
                            value={formData.pincode || ""}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                            inputRef={pincodeRef}
                            error={
                              touched.pincode &&
                              (!!errors.pincode || !!backendErrors.pincode)
                            }
                            placeholder={t("Enter Pincode (e.g.560001)")}
                            name="pincode"
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                            inputProps={{
                              style: {
                                fontFamily: "Roboto",
                                fontWeight: 500,
                                fontSize: "20px",
                                color: "#111214",
                              },
                            }}
                            sx={{
                              padding: "0px 24px",
                              border:
                                touched.pincode &&
                                (errors.pincode || backendErrors.pincode)
                                  ? "2px solid #EE3B2B"
                                  : "2px solid #183059",
                              borderRadius: "16px",
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  border: "none",
                                },
                              },
                            }}
                            disabled={
                              !starting &&
                              !hasPermission("/api/user/organization", "PATCH")
                            }
                          />
                          {touched.pincode &&
                            (errors.pincode || backendErrors.pincode) && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "#EE3B2B",
                                  fontSize: "0.75rem",
                                  marginTop: "4px",
                                  marginLeft: "14px",
                                  display: "block",
                                }}
                              >
                                {errors.pincode || backendErrors.pincode}
                              </Typography>
                            )}
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Upload area taking up the right side */}
                  <Grid
                    item
                    xs={12}
                    md={4}
                    sx={{ display: "flex", flexDirection: "column" }}
                  >
                    <Box
                      sx={{
                        borderRadius: "8px",
                        p: 2,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        position: "relative",
                        border: imagePreview
                          ? "1px solid #201E46"
                          : "2px dashed #ccc",
                        backgroundColor: imagePreview
                          ? "transparent"
                          : "#f8f9fa",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mb: 1,
                          ...(imagePreview ? { height: "150px" } : {}),
                        }}
                      >
                        {imagePreview ? (
                          <div
                            style={{
                              position: "relative",
                              width: "100%",
                              height: "100%",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              background: "#f8f9fa",
                            }}
                          >
                            <img
                              src={imagePreview}
                              alt="User Avatar"
                              style={{
                                width: "80%",
                                height: "80%",
                                objectFit: "cover",
                                borderRadius: "5px",
                              }}
                            />
                            <div
                              style={{
                                position: "absolute",
                                top: "0px",
                                right: "0px",
                                width: "24px",
                                height: "24px",
                                borderRadius: "50%",
                                backgroundColor: "#FFFFFF",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                              }}
                            >
                              <img src={Edit_Icon} alt="edit" />
                            </div>
                          </div>
                        ) : (
                          <img
                            src={Upload_Icon}
                            alt="Upload"
                            style={{ width: 40, height: 40 }}
                          />
                        )}
                      </Box>
                      {!imagePreview && (
                        <Box sx={{ textAlign: "center" }}>
                          <Typography
                            variant="body1"
                            sx={{
                              color: "#1F242E",
                              fontFamily: "Roboto",
                              fontWeight: 600,
                              fontSize: "16px",
                              lineHeight: "100%",
                              mb: 1,
                            }}
                          >
                            {t("Organization Logo")}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#666" }}>
                            {t("Click to upload or drag and drop")}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "#666", fontSize: "12px" }}
                          >
                            {t("PNG, JPG, GIF up to 10MB")}
                          </Typography>
                        </Box>
                      )}

                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          opacity: 0,
                          cursor: "pointer",
                        }}
                        disabled={
                          !starting &&
                          !hasPermission("/api/user/organization", "PATCH")
                        }
                      />
                      {imageUploadError && (
                        <p style={{ color: "red", marginTop: "8px" }}>
                          {imageUploadError}
                        </p>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Box>

            {/* Contact Details Section */}
            <Box>
              <Box
                sx={{
                  width: "100%",
                  height: 52,
                  gap: 16,
                  pt: 1.5,
                  px: 3,
                  pb: 1.5,
                  borderRadius: "8px",
                  bgcolor: "#647490",
                  boxShadow: "-2px 2px 5px 0px #00000040",
                  color: "white",
                  fontSize: "24px",
                  fontWeight: "medium",
                }}
              >
                {t("Contact Details")}
              </Box>

              <Box sx={{ p: 3 }}>
                <Grid container spacing={"20px"}>
                  {/* First row - 3 fields */}
                  <Grid item xs={12} sm={4}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 1,
                        fontFamily: "Roboto",
                        fontWeight: 400,
                        fontSize: "20px",
                        color: "#414958",
                        fontStyle: "Regular",
                      }}
                    >
                      {t("Contact Name")}{" "}
                      <span style={{ color: "#EE3B2B" }}>*</span>
                    </Typography>
                    <TextField
                      required
                      fullWidth
                      id="contactName"
                      value={formData.contactName || ""}
                      onChange={handleInputChange}
                      placeholder={t("Enter Full Name (e.g.Rahul Sharma)")}
                      name="contactName"
                      variant="outlined"
                      inputRef={contactNameRef}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        style: {
                          fontFamily: "Roboto",
                          fontWeight: 500,
                          fontSize: "20px",
                          color: "#111214",
                        },
                      }}
                      sx={{
                        padding: "0px 24px",
                        border: "2px solid #183059",
                        borderRadius: "16px",
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            border: "none",
                          },
                        },
                      }}
                      disabled={
                        !starting &&
                        !hasPermission("/api/user/organization", "PATCH")
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 1,
                        fontFamily: "Roboto",
                        fontWeight: 400,
                        fontSize: "20px",
                        color: "#414958",
                        fontStyle: "Regular",
                      }}
                    >
                      {t("Email")} <span style={{ color: "#EE3B2B" }}>*</span>
                    </Typography>
                    <Box>
                      <TextField
                        required
                        fullWidth
                        id="contactEmail"
                        inputRef={contactEmailRef}
                        value={formData.contactEmail || ""}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        error={
                          touched.contactEmail &&
                          (!!errors.contactEmail ||
                            !!backendErrors.contactEmail)
                        }
                        placeholder={t("Enter Email (e.g.rahul@example.com)")}
                        name="contactEmail"
                        type="email"
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          style: {
                            fontFamily: "Roboto",
                            fontWeight: 500,
                            fontSize: "20px",
                            color: "#111214",
                          },
                        }}
                        sx={{
                          padding: "0px 24px",
                          border:
                            touched.contactEmail &&
                            (errors.contactEmail || backendErrors.contactEmail)
                              ? "2px solid #EE3B2B"
                              : "2px solid #183059",
                          borderRadius: "16px",
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              border: "none",
                            },
                          },
                        }}
                        disabled={
                          !starting &&
                          !hasPermission("/api/user/organization", "PATCH")
                        }
                      />
                      {touched.contactEmail &&
                        (errors.contactEmail || backendErrors.contactEmail) && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#EE3B2B",
                              fontSize: "0.75rem",
                              marginTop: "4px",
                              marginLeft: "14px",
                              display: "block",
                            }}
                          >
                            {errors.contactEmail || backendErrors.contactEmail}
                          </Typography>
                        )}
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 1,
                        fontFamily: "Roboto",
                        fontWeight: 400,
                        fontSize: "20px",
                        color: "#414958",
                        fontStyle: "Regular",
                      }}
                    >
                      {t("Phone Number")}{" "}
                      <span style={{ color: "#EE3B2B" }}>*</span>
                    </Typography>
                    <TextField
                      required
                      fullWidth
                      id="contactPhone"
                      inputRef={contactPhoneRef}
                      value={formData.contactPhone || ""}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      error={
                        touched.contactPhone &&
                        (errors.contactPhone || backendErrors.contactPhone)
                      }
                      placeholder={t("Enter Phone Number (e.g.9876543210)")}
                      name="contactPhone"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        style: {
                          fontFamily: "Roboto",
                          fontWeight: 500,
                          fontSize: "20px",
                          color: "#111214",
                        },
                      }}
                      sx={{
                        padding: "0px 24px",
                        border:
                          touched.contactPhone &&
                          (errors.contactPhone || backendErrors.contactPhone)
                            ? "2px solid #EE3B2B"
                            : "2px solid #183059",
                        borderRadius: "16px",
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            border: "none",
                          },
                        },
                      }}
                      disabled={
                        !starting &&
                        !hasPermission("/api/user/organization", "PATCH")
                      }
                    />
                    {touched.contactPhone &&
                      (errors.contactPhone || backendErrors.contactPhone) && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#EE3B2B",
                            fontSize: "0.75rem",
                            marginTop: "4px",
                            marginLeft: "14px",
                            display: "block",
                          }}
                        >
                          {errors.contactPhone || backendErrors.contactPhone}
                        </Typography>
                      )}
                  </Grid>

                  {/* Second row - 3 fields */}
                  <Grid item xs={12} sm={4}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 1,
                        fontFamily: "Roboto",
                        fontWeight: 400,
                        fontSize: "20px",
                        color: "#414958",
                        fontStyle: "Regular",
                      }}
                    >
                      {t("Employee ID")}
                    </Typography>
                    <TextField
                      required
                      fullWidth
                      id="contactEmployeeId"
                      value={formData.contactEmployeeId || ""}
                      onChange={handleInputChange}
                      placeholder={t("Enter Employee ID (e.g.EMP1234)")}
                      name="contactEmployeeId"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        style: {
                          fontFamily: "Roboto",
                          fontWeight: 500,
                          fontSize: "20px",
                          color: "#111214",
                        },
                      }}
                      sx={{
                        padding: "0px 24px",
                        border: "2px solid #183059",
                        borderRadius: "16px",
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            border: "none",
                          },
                        },
                      }}
                      disabled={
                        !starting &&
                        !hasPermission("/api/user/organization", "PATCH")
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 1,
                        fontFamily: "Roboto",
                        fontWeight: 400,
                        fontSize: "20px",
                        color: "#414958",
                        fontStyle: "Regular",
                      }}
                    >
                      {t("Department")}
                    </Typography>
                    <TextField
                      required
                      fullWidth
                      id="contactDepartment"
                      value={formData.contactDepartment || ""}
                      onChange={handleInputChange}
                      placeholder={"Enter Department (e.g.Human Resources)"}
                      name="contactDepartment"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        style: {
                          fontFamily: "Roboto",
                          fontWeight: 500,
                          fontSize: "20px",
                          color: "#111214",
                        },
                      }}
                      sx={{
                        padding: "0px 24px",
                        border: "2px solid #183059",
                        borderRadius: "16px",
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            border: "none",
                          },
                        },
                      }}
                      disabled={
                        !starting &&
                        !hasPermission("/api/user/organization", "PATCH")
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 1,
                        fontFamily: "Roboto",
                        fontWeight: 400,
                        fontSize: "20px",
                        color: "#414958",
                        fontStyle: "Regular",
                      }}
                    >
                      {t("Designation")}
                    </Typography>
                    <TextField
                      required
                      fullWidth
                      id="contactDesignation"
                      value={formData.contactDesignation || ""}
                      onChange={handleInputChange}
                      placeholder={"Enter Designation (e.g.Manager)"}
                      name="contactDesignation"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        style: {
                          fontFamily: "Roboto",
                          fontWeight: 500,
                          fontSize: "20px",
                          color: "#111214",
                        },
                      }}
                      sx={{
                        padding: "0px 24px",
                        border: "2px solid #183059",
                        borderRadius: "16px",
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            border: "none",
                          },
                        },
                      }}
                      disabled={
                        !starting &&
                        !hasPermission("/api/user/organization", "PATCH")
                      }
                    />
                  </Grid>
                </Grid>
              </Box>
            </Box>

            {/* Transport In-Charge Details Section */}
            <Box>
              <Box
                sx={{
                  width: "100%",
                  height: 52,
                  gap: 16,
                  pt: 1.5,
                  px: 3,
                  pb: 1.5,
                  borderRadius: "8px",
                  bgcolor: "#647490",
                  boxShadow: "-2px 2px 5px 0px #00000040",
                  color: "white",
                  fontSize: "24px",
                  fontWeight: "medium",
                }}
              >
                {t("Transport In-Charge Details")} ({t("Optional")})
              </Box>

              <Box sx={{ p: 3 }}>
                <Grid container spacing={"20px"}>
                  {/* First row - 3 fields */}
                  <Grid item xs={12} sm={4}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 1,
                        fontFamily: "Roboto",
                        fontWeight: 400,
                        fontSize: "20px",
                        color: "#414958",
                        fontStyle: "Regular",
                      }}
                    >
                      {t("Full Name")}
                    </Typography>
                    <TextField
                      fullWidth
                      id="transportFullName"
                      value={formData.transportFullName || ""}
                      onChange={handleInputChange}
                      placeholder={t("Enter Full Name (e.g.Rahul Sharma)")}
                      name="transportFullName"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        style: {
                          fontFamily: "Roboto",
                          fontWeight: 500,
                          fontSize: "20px",
                          color: "#111214",
                        },
                      }}
                      sx={{
                        padding: "0px 24px",
                        border: "2px solid #183059",
                        borderRadius: "16px",
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            border: "none",
                          },
                        },
                      }}
                      disabled={
                        !starting &&
                        !hasPermission("/api/user/organization", "PATCH")
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 1,
                        fontFamily: "Roboto",
                        fontWeight: 400,
                        fontSize: "20px",
                        color: "#414958",
                        fontStyle: "Regular",
                      }}
                    >
                      {t("Email")}
                    </Typography>
                    <Box>
                      <TextField
                        fullWidth
                        id="transportEmail"
                        value={formData.transportEmail || ""}
                        onChange={handleInputChange}
                        onBlur={handleBlur} // Add this
                        error={
                          touched.transportEmail &&
                          (!!errors.transportEmail ||
                            !!backendErrors.transportEmail)
                        } // Update error condition
                        placeholder={t("Enter Email  (e.g.example@gmail.com)")}
                        name="transportEmail"
                        type="email"
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          style: {
                            fontFamily: "Roboto",
                            fontWeight: 500,
                            fontSize: "20px",
                            color: "#111214",
                          },
                        }}
                        sx={{
                          padding: "0px 24px",
                          border:
                            touched.transportEmail &&
                            (errors.transportEmail ||
                              backendErrors.transportEmail)
                              ? "2px solid #EE3B2B"
                              : "2px solid #183059",
                          borderRadius: "16px",
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              border: "none",
                            },
                          },
                        }}
                        disabled={
                          !starting &&
                          !hasPermission("/api/user/organization", "PATCH")
                        }
                      />
                      {touched.transportEmail &&
                        (errors.transportEmail ||
                          backendErrors.transportEmail) && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#EE3B2B",
                              fontSize: "0.75rem",
                              marginTop: "4px",
                              marginLeft: "14px",
                              display: "block",
                            }}
                          >
                            {errors.transportEmail ||
                              backendErrors.transportEmail}
                          </Typography>
                        )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 1,
                        fontFamily: "Roboto",
                        fontWeight: 400,
                        fontSize: "20px",
                        color: "#414958",
                        fontStyle: "Regular",
                      }}
                    >
                      {t("Phone Number")}
                    </Typography>
                    <Box>
                      <TextField
                        fullWidth
                        id="transportPhoneNumber"
                        value={formData.transportPhoneNumber || ""}
                        onChange={handleInputChange}
                        onBlur={handleBlur} // Add this
                        error={
                          touched.transportPhoneNumber &&
                          (errors.transportPhoneNumber ||
                            backendErrors.transportPhoneNumber)
                        }
                        placeholder={t("Enter Phone Number (e.g.9876543210)")}
                        name="transportPhoneNumber"
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          style: {
                            fontFamily: "Roboto",
                            fontWeight: 500,
                            fontSize: "20px",
                            color: "#111214",
                          },
                        }}
                        sx={{
                          padding: "0px 24px",
                          border:
                            touched.transportPhoneNumber &&
                            (errors.transportPhoneNumber ||
                              backendErrors.transportPhoneNumber)
                              ? "2px solid #EE3B2B"
                              : "2px solid #183059",
                          borderRadius: "16px",
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              border: "none",
                            },
                          },
                        }}
                        disabled={
                          !starting &&
                          !hasPermission("/api/user/organization", "PATCH")
                        }
                      />
                      {touched.transportPhoneNumber &&
                        (errors.transportPhoneNumber ||
                          backendErrors.transportPhoneNumber) && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#EE3B2B",
                              fontSize: "0.75rem",
                              marginTop: "4px",
                              marginLeft: "14px",
                              display: "block",
                            }}
                          >
                            {errors.transportPhoneNumber ||
                              backendErrors.transportPhoneNumber}
                          </Typography>
                        )}
                    </Box>
                  </Grid>

                  {/* Second row */}
                  <Grid item xs={12} sm={4}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 1,
                        fontFamily: "Roboto",
                        fontWeight: 400,
                        fontSize: "20px",
                        color: "#414958",
                        fontStyle: "Regular",
                      }}
                    >
                      {t("Employee ID")}
                    </Typography>
                    <TextField
                      fullWidth
                      id="transportEmployeeId"
                      value={formData.transportEmployeeId || ""}
                      placeholder={t("Enter Employee ID (e.g.EMP1024)")}
                      name="transportEmployeeId"
                      onChange={handleInputChange}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        style: {
                          fontFamily: "Roboto",
                          fontWeight: 500,
                          fontSize: "20px",
                          color: "#111214",
                        },
                      }}
                      sx={{
                        padding: "0px 24px",
                        border: "2px solid #183059",
                        borderRadius: "16px",
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            border: "none",
                          },
                        },
                      }}
                      disabled={
                        !starting &&
                        !hasPermission("/api/user/organization", "PATCH")
                      }
                    />
                  </Grid>
                </Grid>
              </Box>
            </Box>
            {(starting || hasPermission("/api/user/organization", "PATCH")) && (
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: "center",
                  marginTop: 2,
                }}
              >
                {/* Centered buttons: Cancel and Save */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    margin: "0 auto",
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={handleCancel}
                    sx={{
                      minWidth: "125px",
                      height: "55px",
                      borderRadius: "8px",
                      borderWidth: "1px",
                      padding: "16px 24px",
                      background: "#FFFFFF",
                      border: "1px solid #414958",
                      color: "#414958",
                      fontFamily: "Roboto",
                      fontWeight: "600",
                      fontSize: "20px",
                      fontStyle: "SemiBold",
                      textTransform: "none",
                      ":hover": {
                        background: "#F2F2F3",
                      },
                    }}
                  >
                    {t("Cancel")}
                  </Button>

                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    sx={{
                      height: "55px",
                      minWidth: "125px",
                      borderRadius: "8px",
                      borderWidth: "1px",
                      padding: "16px 24px",
                      background: "#F9E719",
                      border: "1px solid #414958",
                      color: "#111214",
                      fontFamily: "Roboto",
                      fontWeight: "600",
                      fontSize: "20px",
                      fontStyle: "SemiBold",
                      textTransform: "none",
                      ":hover": {
                        background: "#F9E719",
                      },
                    }}
                  >
                    {isSubmitting ? (
                      <CircularProgress size={24} sx={{ color: "#1F242E" }} />
                    ) : (
                      t("Save")
                    )}
                  </Button>
                </Box>

                {/* Right aligned button */}
                {starting && (
                  <Button
                    variant="contained"
                    onClick={() => navigate("/registerfeature")}
                    sx={{
                      height: 55,
                      borderRadius: "8px",
                      borderWidth: "1px",
                      padding: "16px 24px",
                      background: "#F9E719",
                      border: "1px solid #414958",
                      color: "#1F242E",
                      fontFamily: "Roboto",
                      fontWeight: "500",
                      fontSize: "20px",
                      textTransform: "none",
                      marginTop: { xs: 2, sm: 0 },
                      "&:hover": { background: "#F9E719" },
                    }}
                  >
                    Share Your Requirement
                  </Button>
                )}
              </Box>
            )}
          </Box>
        </Paper>
        <Box sx={{ height: "10px" }}></Box>
      </Box>
      {/* <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar> */}
    </>
  );
}
